'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useEffect, useId, useState } from 'react';

import cx from 'clsx';
import { useRouter } from 'next-nprogress-bar';
import PropTypes from 'prop-types';

import SvgIcon from 'components/ui/SvgIcon';
import Text from 'components/ui/Text';

import useDetectKeyboard from 'hooks/useDetectKeyboard';
import useDetectTouch from 'hooks/useDetectTouch';

import PopularSearches from './PopularSearches';

import styles from './SearchBar.module.scss';

const SearchBar = ({
    className,
    popularSearchesMobileClassName,
    onIsOpen,
    onSubmitted,
    onClick,
    isActive,
    popularSearches = [],
}) => {
    const inputRef = React.useRef(null);
    const rootRef = React.useRef(null);
    const router = useRouter();
    const [isOpen, setIsOpen] = useState(false);
    const [openedViaKeyboard, setOpenedViaKeyboard] = useState(false);
    const isTouch = useDetectTouch();
    const isKeyboard = useDetectKeyboard();
    const searchId = `site-search-${useId()}`;

    const handleSubmit = e => {
        e.preventDefault();
        router.push(`/search?q=${encodeURIComponent(e.target.search.value)}`);

        setIsOpen(false);
        setOpenedViaKeyboard(false); // Reset when search is submitted
        e.target.search.value = '';
        onSubmitted && onSubmitted();
    };

    const handlePopularSearchesClick = (e, searchTerm) => {
        e.preventDefault();
        router.push(`/search?q=${encodeURIComponent(searchTerm)}`);
        setIsOpen(false);
        setOpenedViaKeyboard(false);
        onSubmitted && onSubmitted();
    };

    const handleMouseEnter = () => {
        setIsOpen(true);
    };

    const handleMouseLeave = () => {
        setIsOpen(false);
        setOpenedViaKeyboard(false); // Reset when mouse leaves
    };

    const handleFocus = () => {
        setIsOpen(true);
        // Track if this was opened via keyboard navigation
        if (isKeyboard) {
            setOpenedViaKeyboard(true);
        }
    };

    const handleBlur = e => {
        // Don't close if focus moved to another element inside the search bar
        if (e.currentTarget.contains(e.relatedTarget)) {
            return;
        }

        // Don't close if the mouse is still hovering over the search bar
        // (e.g. clicking a button in Chrome/Safari where blur fires on mousedown)
        if (rootRef.current?.matches(':hover')) {
            return;
        }

        setIsOpen(false);
        setOpenedViaKeyboard(false); // Reset when focus is lost
    };

    useEffect(() => {
        onIsOpen && onIsOpen(isOpen);
    }, [onIsOpen, isOpen]);

    // Only auto-focus for mouse interactions, not keyboard navigation
    // Also prevent mouse auto-focus if search was originally opened via keyboard
    useEffect(() => {
        if (!isTouch && !isKeyboard && !openedViaKeyboard) {
            if (isOpen) {
                inputRef.current?.focus();
            } else {
                inputRef.current?.blur();
            }
        }
    }, [isOpen, isTouch, isKeyboard, openedViaKeyboard]);

    const handleMobileInputClick = e => {
        e.preventDefault();
        onClick && onClick();

        // force focus on input with no scroll to prevent ios jump
        inputRef.current.focus({ preventScroll: true });
    };

    return (
        <>
            <div
                ref={rootRef}
                className={cx(styles.root, className, {
                    [styles.isOpen]: isOpen,
                    [styles.isActive]: isActive,
                })}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onFocus={handleFocus}
                onBlur={handleBlur}
            >
                <div className={styles.rootInner}>
                    <Text
                        as="span"
                        className={styles.searchIconWrapper}
                        baseTheme="bodyMedium"
                        aria-hidden="true"
                    >
                        <SvgIcon className={styles.searchIcon} type="search" />
                    </Text>
                    <aside
                        className={styles.overlay}
                        aria-hidden={!isOpen}
                        inert={!isOpen ? true : undefined}
                    >
                        <form
                            role="search"
                            onSubmit={handleSubmit}
                            className={styles.searchWrapper}
                        >
                            <div className={styles.searchInput}>
                                <div className={styles.searchInputInner}>
                                    <Text as="span" baseTheme="bodyMedium">
                                        <input
                                            ref={inputRef}
                                            id={searchId}
                                            name="search"
                                            autoComplete="off"
                                            type="text"
                                            enterKeyHint="search"
                                            placeholder="Type to search"
                                            className={styles.input}
                                            aria-label="Search the site"
                                        />
                                    </Text>
                                    <button
                                        className={styles.submitButtonWrapper}
                                        type="submit"
                                        aria-label="Submit search"
                                        onMouseDown={e => e.preventDefault()}
                                    >
                                        <div
                                            className={
                                                styles.submitButtonMobile
                                            }
                                            aria-hidden="true"
                                        />
                                        <div
                                            className={
                                                styles.submitButtonMobileLight
                                            }
                                            aria-hidden="true"
                                        />
                                        <div
                                            className={
                                                styles.submitButtonDesktop
                                            }
                                            aria-hidden="true"
                                        />
                                        <span className="sr-only">Search</span>
                                    </button>
                                    <button
                                        className={styles.fakeInputButton}
                                        onClick={handleMobileInputClick}
                                    >
                                        <span className="sr-only">
                                            Search the site
                                        </span>
                                    </button>
                                </div>
                                {popularSearches.length > 0 && (
                                    <PopularSearches
                                        className={
                                            styles.popularSearchesDesktop
                                        }
                                        items={popularSearches}
                                        onItemClick={handlePopularSearchesClick}
                                    />
                                )}
                            </div>
                        </form>
                    </aside>
                </div>
            </div>
            {popularSearches.length > 0 && (
                <PopularSearches
                    className={cx(
                        styles.popularSearchesMobile,
                        popularSearchesMobileClassName,
                        {
                            [styles.isActive]: isActive,
                        }
                    )}
                    aria-hidden={!isActive}
                    inert={!isActive ? true : undefined}
                    items={popularSearches}
                    onItemClick={handlePopularSearchesClick}
                />
            )}
        </>
    );
};

SearchBar.propTypes = {
    className: PropTypes.string,
    onIsOpen: PropTypes.func,
    popularSearchesMobileClassName: PropTypes.string,
    onSubmitted: PropTypes.func,
    popularSearches: PropTypes.arrayOf(PropTypes.string),
};

export default SearchBar;
